/* /letters — bilingual letter templates, generate/print, request queue with issue (S3 document). */
import "./pay.css";
import { useState } from "react";
import { useData } from "../../data";
import { useCreateLetter, useIssueLetter, useLetters, useSettings, useToday } from "../../data/hooks";
import type { Employee, Letter, Tenant } from "../../data/types";
import { useOverlays } from "../../app/overlays";
import { useI18n, LOOKUP } from "../../i18n";
import { fmtN, gDate, hDate } from "../../lib/format";
import { Button, Card, EmptyState, ErrorBox, Icon, IconButton, PageHeader, Person, SelectField, Skeleton, TextField, cx, errMsg, useToast } from "../../ui";
import { StatusChip, unknownEmp, useEmpMap, useRole } from "./components/common";
import { curCode, curWordAr, idLabel } from "../../lib/country";

export const LETTER_KINDS: [string, string, string, string, string][] = [
  ["Salary certificate", "تعريف بالراتب", "banknote", "For banks, embassies and leasing", "للبنوك والسفارات والإيجار"],
  ["Experience letter", "شهادة خبرة", "award", "Service period and role", "مدة الخدمة والمسمى"],
  ["No objection certificate", "خطاب عدم ممانعة", "shield", "Travel, studies or transfer", "للسفر أو الدراسة أو النقل"],
  ["Salary transfer letter", "خطاب تحويل راتب", "credit", "Bank salary transfer undertaking", "تعهد بتحويل الراتب للبنك"],
  ["Embassy letter", "خطاب للسفارة", "globe", "Visa support for travel", "دعم طلب التأشيرة للسفر"],
];
const kindAr = (k: string) => LETTER_KINDS.find((x) => x[0] === k)?.[1] ?? k;

function LetterPaper({ kind, e, tenant, today, to }: { kind: string; e: Employee; tenant?: Tenant; today: string; to: string }) {
  const wage = e.basic + e.housing + e.transport + e.other;
  const nat = LOOKUP.NAT[e.nationality] ?? [e.nationality, e.nationality];
  const titleAr = LOOKUP.TITLE_AR[e.title] ?? e.title;
  const co = tenant?.name ?? "the company", coAr = tenant?.nameAr ?? "المنشأة";
  let en: React.ReactNode, ar: React.ReactNode;
  if (kind === "Salary certificate" || kind === "Salary transfer letter") {
    en = <>This is to certify that <b>{e.nameEn}</b>, {nat[0]} national, {idLabel(e.nationality)[0]} no. {e.idNumber}, has been employed by {co} since {gDate(e.joined, "en")} as <b>{e.title}</b>, with a monthly salary of {curCode()} {fmtN(wage)} (basic {curCode()} {fmtN(e.basic)}, housing {curCode()} {fmtN(e.housing)}, transport {curCode()} {fmtN(e.transport)}).{kind === "Salary transfer letter" && <> We undertake to transfer the salary to IBAN {e.iban.replace(/\s/g, "")} until further notice.</>}</>;
    ar = <>تشهد {coAr} بأن السيد/ة <b>{e.nameAr}</b>، {nat[1]} الجنسية، يعمل لديها منذ {gDate(e.joined, "ar")} بمسمى {titleAr}، براتب شهري إجمالي قدره {fmtN(wage)} {curWordAr()}.{kind === "Salary transfer letter" && <> ونتعهد بتحويل الراتب إلى الآيبان {e.iban.replace(/\s/g, "")} حتى إشعار آخر.</>}</>;
  } else if (kind === "Experience letter") {
    en = <>This is to certify that <b>{e.nameEn}</b> has worked with {co} as <b>{e.title}</b> in the {LOOKUP.DEPT[e.dept]?.[0] ?? e.dept} department from {gDate(e.joined, "en")} to date. During this period we found them diligent, honest and professional.</>;
    ar = <>تشهد {coAr} بأن السيد/ة <b>{e.nameAr}</b> عمل لديها بمسمى {titleAr} منذ {gDate(e.joined, "ar")} وحتى تاريخه، وكان مثالًا للجدية والأمانة.</>;
  } else if (kind === "No objection certificate") {
    en = <>{co} has <b>no objection</b> to <b>{e.nameEn}</b>, {idLabel(e.nationality)[0]} no. {e.idNumber}, regarding the matter addressed to {to}.</>;
    ar = <>لا مانع لدى {coAr} فيما يخص السيد/ة <b>{e.nameAr}</b> بخصوص الموضوع الموجه إلى {to}.</>;
  } else {
    en = <>We confirm that <b>{e.nameEn}</b>, {idLabel(e.nationality)[0]} no. {e.idNumber}, is employed with {co} as <b>{e.title}</b> since {gDate(e.joined, "en")}, and we kindly request that the necessary visa be granted.</>;
    ar = <>نفيد بأن السيد/ة <b>{e.nameAr}</b> يعمل لدى {coAr} بمسمى {titleAr} منذ {gDate(e.joined, "ar")}، ونرجو التكرم بمنحه التأشيرة اللازمة.</>;
  }
  return (
    <article className="paper printable pay-letter" dir="ltr" data-testid="letter-preview">
      <div className="paper-head">
        <div className="row nw" style={{ gap: 12, alignItems: "flex-start" }}><span className="seal">{co.slice(0, 1)}</span><div><h2>{co}</h2>{tenant && <div className="muted" style={{ fontSize: 12 }}>CR {tenant.cr} · {tenant.city}</div>}</div></div>
        <div className="ar" style={{ textAlign: "end" }}><h2>{coAr}</h2><div className="muted" style={{ fontSize: 12 }}>{hDate(today)}</div></div>
      </div>
      <div className="bi" style={{ marginBottom: 14 }}><b>{kind.toUpperCase()}</b><b className="ar">{kindAr(kind)}</b></div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 24 }}>
        <p style={{ margin: 0 }}>To: {to || "To whom it may concern"},<br />{en}<br /><br />This letter was issued at the employee&apos;s request without any liability on the establishment.</p>
        <p className="ar" dir="rtl" style={{ margin: 0 }}>إلى: {to || "من يهمه الأمر"}،<br />{ar}<br /><br />وقد أُعطي هذا الخطاب بناءً على طلبه دون أدنى مسؤولية على المنشأة.</p>
      </div>
      <div className="bi" style={{ marginTop: 28, alignItems: "flex-end" }}>
        <div><div className="muted" style={{ fontSize: 12 }}>Ref. HR/{today.slice(0, 4)}/{300 + e.id}</div><div style={{ fontSize: 12 }}>{gDate(today, "en")}</div></div>
        <div style={{ textAlign: "center" }}><div className="muted" style={{ fontSize: 12 }}>HR Manager · مدير الموارد البشرية</div></div>
      </div>
    </article>
  );
}

export default function Letters() {
  const { t, L, name, isAr, lang } = useI18n();
  const toast = useToast();
  const data = useData();
  const today = useToday();
  const { openPrint } = useOverlays();
  const { can } = useRole();
  const q = useLetters();
  const emps = useEmpMap();
  const setQ = useSettings();
  const create = useCreateLetter();
  const issue = useIssueLetter();
  const [kind, setKind] = useState(LETTER_KINDS[0][0]);
  const [emp, setEmp] = useState("");
  const [to, setTo] = useState<string | null>(null);
  const [toErr, setToErr] = useState<string | undefined>();
  const [busy, setBusy] = useState<number | null>(null);

  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return <Skeleton />;
  const empId = Number(emp || emps.list[0]?.id || 0);
  const e = emps.map.get(empId);
  const toVal = to ?? L("To whom it may concern", "إلى من يهمه الأمر");

  const createReq = async () => {
    if (!toVal.trim()) { setToErr("required"); return; }
    try {
      await create.mutateAsync({ employeeId: empId, kind, to: toVal.trim() });
      toast(L("Letter request created", "تم إنشاء طلب الخطاب"));
    } catch (err) {
      const f = (err as { fields?: Record<string, string> }).fields;
      if (f?.to) setToErr(f.to); else toast(errMsg(err), "bad");
    }
  };
  const doIssue = (l: Letter) => {
    setBusy(l.id);
    issue.mutate(l.id, {
      onSuccess: () => { setKind(l.kind); setEmp(String(l.employeeId)); setTo(l.to); toast(L(l.kind + " issued for " + (emps.map.get(l.employeeId)?.nameEn ?? ""), "تم إصدار " + kindAr(l.kind) + " لـ " + (emps.map.get(l.employeeId)?.nameAr ?? ""))); },
      onError: (err) => toast(errMsg(err), "bad"),
      onSettled: () => setBusy(null),
    });
  };

  return (
    <>
      <PageHeader title={t("Letters & requests")} sub={L("Bilingual templates · employees can also request them in self-service", "قوالب ثنائية اللغة · يمكن للموظفين طلبها من الخدمة الذاتية")} />
      <div className="g g-3 mb" role="radiogroup" aria-label={L("Letter template", "قالب الخطاب")}>
        {LETTER_KINDS.map(([k, ar, icon, dEn, dAr]) => (
          <button key={k} type="button" role="radio" aria-checked={kind === k} className={cx("tpl", kind === k && "on")} onClick={() => setKind(k)} data-testid={"tpl-" + k.toLowerCase().replace(/ /g, "-")}>
            <span className="kpi-ico"><Icon name={icon} /></span><b>{isAr ? ar : k}</b><span className="sm muted">{L(dEn, dAr)}</span>
          </button>
        ))}
      </div>
      <div className="g g-1-2">
        <div className="stack lg">
          <Card title={L("Generate", "إنشاء")} testId="letter-generate">
            <div className="stack">
              <SelectField label={t("Employee")} value={String(empId || "")} onChange={setEmp} options={emps.list.map((x) => [x.id, name(x) + " · " + x.code])} testId="letter-emp" />
              <TextField label={L("Addressed to", "موجه إلى")} value={toVal} onChange={(v) => { setTo(v); setToErr(undefined); if (!v.trim()) setToErr("required"); }} err={toErr} testId="letter-to" />
              <div className="row">
                <Button variant="pri" icon="printer" onClick={() => openPrint({ doc: "letter", emp: empId, letter: kind })} disabled={!e} data-testid="btn-print-letter">{t("Print")}</Button>
                <Button icon="plus" onClick={createReq} loading={create.isPending} disabled={!e} data-testid="btn-create-letter">{L("Add to queue", "إضافة للطلبات")}</Button>
              </div>
            </div>
          </Card>
          <Card title={L("Requests from employees", "طلبات الموظفين")} testId="letter-queue">
            {q.data.length === 0 ? <EmptyState title={L("No letter requests", "لا توجد طلبات خطابات")} /> : q.data.map((l) => (
              <div className="list-row" key={l.id} data-testid={"letter-row-" + l.id}>
                <Person e={emps.map.get(l.employeeId) ?? unknownEmp(l.employeeId)} meta={(isAr ? kindAr(l.kind) : l.kind) + " · " + l.to + " · " + gDate(l.date, lang)} />
                <span className="spacer" />
                {l.status === "Pending" && can("letters.issue")
                  ? <Button variant="sm" onClick={() => doIssue(l)} loading={busy === l.id} data-testid={"btn-issue-" + l.id}>{L("Issue", "إصدار")}</Button>
                  : <span className="row nw">
                      <StatusChip status={l.status} />
                      {l.fileKey && <a className="ibtn" href={data.fileUrl(l.fileKey)} target="_blank" rel="noreferrer" aria-label={L("Download letter", "تنزيل الخطاب")} title={L("Download letter", "تنزيل الخطاب")} data-testid={"letter-file-" + l.id}><Icon name="download" /></a>}
                      <IconButton icon="printer" label={L("Print letter", "طباعة الخطاب")} onClick={() => openPrint({ doc: "letter", emp: l.employeeId, letter: l.kind })} data-testid={"btn-print-" + l.id} />
                    </span>}
              </div>
            ))}
          </Card>
        </div>
        <div>{e ? <LetterPaper kind={kind} e={e} tenant={setQ.data?.tenant} today={today} to={toVal} /> : <Skeleton />}</div>
      </div>
    </>
  );
}
